/**
 * Company manager sidebar: accounts + browse ponds / sampling like Super Admin (scoped to managed orgs).
 */
export const COMPANY_MENU = [
  {
    path: "/dashboard",
    icon: "fa-solid fa-chart-line",
    title: "Dashboard",
    section: "menu",
  },
  {
    path: "/company/accounts",
    icon: "fa-solid fa-building",
    title: "Company Accounts",
    section: "menu",
    activeMatchPaths: [
      "/company/accounts",
      "/company/accounts/create",
      "/company/accounts/:id",
      "/company/accounts/:id/renew",
      "/company/accounts/:id/extra-pond",
      "/company/staff/:fisherId",
    ],
  },
  {
    path: "/pond",
    icon: "fas fa-swimming-pool",
    title: "Ponds",
    section: "menu",
    activeMatchPaths: [
      "/pond",
      "/updatePond/:id",
      "/pondDetailViaPond/:id",
    ],
  },
  {
    path: "/fishingData",
    icon: "fa-solid fa-fish",
    title: "Fishing/Sampling Data",
    section: "menu",
    activeMatchPaths: [
      "/fishingData",
      "/sampling",
      "/pondDetailViaPond/:id",
      "/pondSamplingUpload/:pondId",
      "/UpdatePD/:id",
      "/Updatepd/:id",
      "/samplingViaPondDetail/:id",
      "/samplingGalleryViaPondDetail/:id",
      "/pondNoteViaPondDetail/:id",
      "/pondFishViaPondDetail/:id",
      "/pondNote",
      "/AddSamplingDetail/:id",
      "/UpdateSamplingDetail/:id",
      "/createpd",
      "/createpd/:id",
    ],
  },
  {
    path: "/FishDataTableView",
    icon: "fa-solid fa-table",
    title: "Table View",
    section: "menu",
    activeMatchPaths: ["/FishDataTableView", "/FishDataTableView/:id"],
  },
  {
    title: "Graph View",
    path: "/fish-data-graph-new",
    icon: "fas fa-chart-line",
    section: "menu",
    activeMatchPaths: [
      "/fish-data-graph-new",
      "/fish-data-graph-new/:pondDetailId",
      "/fish-data-graph",
      "/fish-data-graph/:pondDetailId",
      "/FishDataGraphView",
      "/FishDataGraphView/:id",
    ],
  },
  { path: "/profile", icon: "fa-solid fa-user", title: "Profile", section: "general" },
];

/**
 * Sampling-only menu for company-managed owners who self-login.
 * Company/Super with a scoped account use the full owner Menu instead.
 */
export const MANAGED_OWNER_MENU = [
  {
    path: "/fishingData",
    icon: "fa-solid fa-fish",
    title: "Fishing/Sampling Data",
    section: "menu",
    activeMatchPaths: [
      "/fishingData",
      "/sampling",
      "/pondDetailViaPond/:id",
      "/pondSamplingUpload/:pondId",
      "/createpd",
      "/createpd/:id",
      "/UpdatePD/:id",
      "/Updatepd/:id",
      "/samplingViaPondDetail/:id",
      "/samplingGalleryViaPondDetail/:id",
      "/AddSamplingDetail/:id",
      "/UpdateSamplingDetail/:id",
      "/pondNoteViaPondDetail/:id",
      "/pondFishViaPondDetail/:id",
      "/pondNote",
    ],
  },
  {
    path: "/FishDataTableView",
    icon: "fa-solid fa-table",
    title: "Table View",
    section: "menu",
    activeMatchPaths: ["/FishDataTableView", "/FishDataTableView/:id"],
  },
  {
    title: "Graph View",
    path: "/fish-data-graph-new",
    icon: "fas fa-chart-line",
    section: "menu",
    activeMatchPaths: [
      "/fish-data-graph-new",
      "/fish-data-graph-new/:pondDetailId",
      "/fish-data-graph",
      "/fish-data-graph/:pondDetailId",
      "/FishDataGraphView",
      "/FishDataGraphView/:id",
    ],
  },
];

export const SAMPLING_ONLY_PATH_PREFIXES = [
  "/fishingData",
  "/sampling",
  "/pondDetailViaPond",
  "/pondSamplingUpload",
  "/createpd",
  "/UpdatePD",
  "/Updatepd",
  "/samplingViaPondDetail",
  "/samplingGalleryViaPondDetail",
  "/AddSamplingDetail",
  "/UpdateSamplingDetail",
  "/pondNoteViaPondDetail",
  "/pondFishViaPondDetail",
  "/pondNote",
  "/FishDataTableView",
  "/fish-data-graph-new",
  "/fish-data-graph",
  "/FishDataGraphView",
  "/user/admin-handoff",
  "/user/login",
  "/user/logout",
];

export function isSamplingOnlyAllowedPath(pathname) {
  if (!pathname) return false;
  if (pathname === "/" || pathname === "") return true;
  return SAMPLING_ONLY_PATH_PREFIXES.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`)
  );
}

/**
 * `path` = Link target. `activeMatchPaths` = optional list for Route matching so nested screens
 * still highlight the correct sidebar item (React Router 5 accepts path as string | string[]).
 * `section` = sidebar group: "menu" | "general"
 */
const Menu = [
  { path: "/dashboard", icon: "fa-solid fa-chart-line", title: "Dashboard", exact: true, section: "menu" },
  {
    path: "/pond",
    icon: "fas fa-swimming-pool",
    title: "Pond",
    section: "menu",
    activeMatchPaths: [
      "/pond",
      "/createPond",
      "/updatePond/:id",
      "/pondFeedFertilization/:id",
      "/pondStocking/:id",
    ],
  },
  {
    path: "/fishingData",
    icon: "fa-solid fa-fish",
    title: "Fishing/Sampling Data",
    section: "menu",
    activeMatchPaths: [
      "/fishingData",
      "/sampling",
      "/pondDetailViaPond/:id",
      "/pondSamplingUpload/:pondId",
      "/createpd",
      "/createpd/:id",
      "/UpdatePD/:id",
      "/Updatepd/:id",
      "/samplingViaPondDetail/:id",
      "/samplingGalleryViaPondDetail/:id",
      "/AddSamplingDetail/:id",
      "/UpdateSamplingDetail/:id",
      "/pondNoteViaPondDetail/:id",
      "/pondFishViaPondDetail/:id",
      "/pondNote",
    ],
  },
  {
    path: "/FishDataTableView",
    icon: "fa-solid fa-table",
    title: "Table View",
    section: "menu",
    activeMatchPaths: ["/FishDataTableView", "/FishDataTableView/:id"],
  },
  {
    title: "Graph View",
    path: "/fish-data-graph-new",
    icon: "fas fa-chart-line",
    section: "menu",
    activeMatchPaths: [
      "/fish-data-graph-new",
      "/fish-data-graph-new/:pondDetailId",
      "/fish-data-graph",
      "/fish-data-graph/:pondDetailId",
      "/FishDataGraphView",
      "/FishDataGraphView/:id",
    ],
  },
  {
    path: "/staff",
    icon: "fa-solid fa-users",
    title: "Users",
    section: "general",
    activeMatchPaths: [
      "/staff",
      "/createStaff",
      "/inviteScientist",
      "/UpdateStaff/:id",
      "/ScientistPond/:id",
      "/changePassword/:id",
    ],
  },
  {
    path: "/scientistInvites",
    icon: "fa-solid fa-envelope-open-text",
    title: "Invitations",
    section: "general",
    activeMatchPaths: ["/scientistInvites"],
  },
  {
    path: "/join-consultancy",
    icon: "fa-solid fa-handshake",
    title: "Join consultancy",
    section: "general",
    activeMatchPaths: ["/join-consultancy"],
  },
  { path: "/profile", icon: "fa-solid fa-user", title: "Profile", section: "general" },
  {
    path: "/packages",
    icon: "fa-solid fa-boxes-packing",
    title: "Package",
    section: "general",
    activeMatchPaths: ["/packages", "/payment/:id"],
  },
];

export default Menu;
